import React, { useEffect, useRef } from 'react'

import { ReactComponent as ChevronIcon } from '@sketch/icons/chevron-right-16'

import { EntityChangeIcon } from '../../changeIcon'
import { describeChanges, describeOnlyIn } from '../../describeChange'
import { EntityChange, EntityChangeKind, LayerChange } from '../../layerTypes'
import * as Styled from './ChangesPanel.styles'
import { LayerRow } from './LayerRow'

/**
 * What an entity's own change amounts to, in one line. Something only one of
 * the two versions has names that version rather than saying what happened to
 * it, because "added" reads as a thing the reader did when they are standing on
 * the version that has it.
 */
function describeEntity(change: EntityChange) {
  if (change.kind !== 'modified') return describeOnlyIn(change.kind)

  const count = change.layerChangeCount
  const inside =
    count === 0
      ? ''
      : count === 1
        ? '1 layer changed'
        : `${count} layers changed`

  // Both, when both happened: a frame renamed with three layers changed inside
  // it is not described by either half alone.
  return [describeChanges(change.changes), inside].filter(Boolean).join(', ')
}

interface EntityRowProps {
  change: EntityChange
  /** The changed layers inside it, listed under it while open. */
  layers: readonly LayerChange[]
  isSelected: boolean
  isOpen: boolean
  /** The layer selected anywhere in the panel, if it is one of these. */
  selectedLayerPathHash?: string
  /** Whether a change of this kind is only in the version not on screen. */
  isFromComparedVersion(kind: EntityChangeKind): boolean
  onToggle(): void
  onSelect(): void
  onSelectLayer(layer: LayerChange): void
}

/** A changed frame or Section, with the layers that changed inside it. */
export function EntityRow({
  change,
  layers,
  isSelected,
  isOpen,
  selectedLayerPathHash,
  isFromComparedVersion,
  onToggle,
  onSelect,
  onSelectLayer,
}: EntityRowProps) {
  const name = change.name || 'Untitled'
  const isCompared = isFromComparedVersion(change.kind)

  // The canvas selects these too, and a selection off the end of a scrolled
  // list reads as nothing having happened. `nearest` leaves a row already in
  // view alone, so clicking one here does not scroll the list underneath.
  const row = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isSelected) row.current?.scrollIntoView({ block: 'nearest' })
  }, [isSelected])

  return (
    <Styled.Row>
      <Styled.RowHeader>
        {/* Only where there is something to fold: a twist-down that does
        nothing is a control the reader has to try to learn. */}
        {layers.length > 0 ? (
          <Styled.Disclosure
            type="button"
            $open={isOpen}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Hide' : 'Show'} the layers changed in ${name}`}
            onClick={onToggle}
          >
            <ChevronIcon aria-hidden />
          </Styled.Disclosure>
        ) : (
          <Styled.DisclosureSpacer aria-hidden />
        )}

        <Styled.ChangeButton
          ref={row}
          type="button"
          $selected={isSelected}
          $compared={isCompared}
          aria-pressed={isSelected}
          onClick={onSelect}
        >
          <Styled.RowIcon
            aria-hidden
            $selected={isSelected}
            $compared={isCompared}
          >
            <EntityChangeIcon change={change} />
          </Styled.RowIcon>
          <Styled.Text>
            <Styled.Name $selected={isSelected} $compared={isCompared}>
              {name}
            </Styled.Name>
            <Styled.What>{describeEntity(change)}</Styled.What>
          </Styled.Text>
        </Styled.ChangeButton>
      </Styled.RowHeader>

      {isOpen &&
        layers.map(layer => (
          <LayerRow
            key={layer.layerPathHash}
            change={layer}
            isSelected={selectedLayerPathHash === layer.layerPathHash}
            isFromComparedVersion={isFromComparedVersion(layer.kind)}
            onSelect={() => onSelectLayer(layer)}
          />
        ))}
    </Styled.Row>
  )
}
